<!-- eslint-disable vue/no-bare-strings-in-template @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, ref, watch } from 'vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false },
  state: { type: String, default: 'form' }, // 'form' | 'success' | 'error'
  variant: { type: String, default: 'list' }, // 'list' | 'shop_window'
  mode: { type: String, default: 'create' }, // 'create' | 'edit'
  initialName: { type: String, default: '' },
});

const emit = defineEmits([
  'close',
  'submit',
  'retry',
  'go-to-overview',
  'view-detail',
  'continue-search',
]);

const name = ref('');
const trimmed = computed(() => name.value.trim());
const length = computed(() => trimmed.value.length);
const valid = computed(() => length.value >= 3 && length.value <= 40);

watch(
  () => props.show,
  value => {
    if (value) name.value = props.initialName || '';
  }
);

const submit = () => {
  if (!valid.value) return;
  emit('submit', trimmed.value);
};

const isShopWindow = computed(() => props.variant === 'shop_window');
const isEditMode = computed(() => props.mode === 'edit');
const titleLabel = computed(() =>
  isShopWindow.value ? 'Nome da vitrine' : 'Nome da lista'
);
const dialogTitle = computed(() => {
  if (!isShopWindow.value)
    return isEditMode.value ? 'Editar lista' : 'Criar lista';
  return isEditMode.value ? 'Editar vitrine' : 'Criar vitrine';
});
const submitLabel = computed(() => (isEditMode.value ? 'Salvar' : 'Criar'));
const overviewLabel = computed(() =>
  isShopWindow.value ? 'Ir para minhas vitrines' : 'Ir para Minhas listas'
);
const detailLabel = computed(() =>
  isShopWindow.value ? 'Ver vitrine' : 'Ver lista'
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template @intlify/vue-i18n/no-raw-text -->
  <LaisModal
    :show="show"
    :title="dialogTitle"
    max-width-class="max-w-[480px]"
    @close="emit('close')"
  >
    <template v-if="state === 'success'">
      <p class="text-[13px] leading-6 text-emerald-700 font-semibold">
        Tudo certo!
      </p>
      <p class="text-[13px] leading-6 text-lais-text-muted">
        Sua {{ isShopWindow ? 'vitrine' : 'lista' }} foi
        {{ isEditMode ? 'atualizada' : 'criada' }} com sucesso.
      </p>
    </template>

    <template v-else-if="state === 'error'">
      <p class="text-[13px] leading-6 text-rose-700">
        Ops, algo deu errado ao criar.
      </p>
    </template>

    <template v-else>
      <LaisField :label="titleLabel" helper="Exemplo: apartamentos no Centro">
        <input
          v-model="name"
          type="text"
          maxlength="40"
          class="w-full rounded-lais border border-lais-border bg-white px-3 py-2 text-[14px]"
          placeholder="Exemplo: apartamentos no Centro"
          @keydown.enter.prevent="submit"
        />
      </LaisField>
      <div class="flex items-center justify-between text-[12px] mt-2">
        <span class="text-lais-text-muted">Mínimo 3 caracteres.</span>
        <span
          :class="
            length > 40 ? 'text-rose-700 font-semibold' : 'text-lais-text-muted'
          "
        >
          {{ length }}/40
        </span>
      </div>
    </template>

    <template #footer>
      <div class="flex flex-wrap justify-end gap-2">
        <template v-if="state === 'success'">
          <LaisPrimaryButton
            variant="ghost"
            icon="i-lucide-search"
            @click="emit('continue-search')"
          >
            Continuar buscando
          </LaisPrimaryButton>
          <LaisPrimaryButton
            variant="ghost"
            icon="i-lucide-list"
            @click="emit('go-to-overview')"
          >
            {{ overviewLabel }}
          </LaisPrimaryButton>
          <LaisPrimaryButton
            icon="i-lucide-arrow-right"
            @click="emit('view-detail')"
          >
            {{ detailLabel }}
          </LaisPrimaryButton>
        </template>
        <template v-else-if="state === 'error'">
          <LaisPrimaryButton icon="i-lucide-refresh-cw" @click="emit('retry')">
            Tentar novamente
          </LaisPrimaryButton>
        </template>
        <template v-else>
          <LaisPrimaryButton variant="ghost" @click="emit('close')">
            Cancelar
          </LaisPrimaryButton>
          <LaisPrimaryButton
            icon="i-lucide-plus"
            :disabled="!valid"
            :is-loading="isLoading"
            @click="submit"
          >
            {{ submitLabel }}
          </LaisPrimaryButton>
        </template>
      </div>
    </template>
  </LaisModal>
</template>
